// @flow
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import BlockList from 'ui/molecules/BlockList'
import DateField from 'ui/molecules/DateField'
import TextAreaField from 'ui/molecules/TextAreaField'
import TextField from 'ui/molecules/TextField'
import TimeField from 'ui/molecules/TimeField'
import NumericText from 'ui/NumericText'
import SingleLinePhone from 'ui/SingleLinePhone'
import { QuestionTitle } from 'ui/Text'
import useBuilderQuestion from 'utils/FormBuilder/hooks/useBuilderQuestion'
import { emailSchema, phoneSchema } from 'utils/schemas'

function BuilderSideMenuDefaultValue(): React.Node {
  const { selectedItem, setQuestion } = React.useContext(FormBuilderContext)
  const question = useBuilderQuestion(selectedItem?.id)
  const [error, setError] = React.useState('')

  const onChangeDateDefaultValue = React.useCallback(
    (nextValue, isNow) => {
      setQuestion(selectedItem.id, {
        ...question,
        default_value: nextValue,
        date_default_value_now: isNow,
      })
    },
    [question, selectedItem, setQuestion]
  )

  const onChangeDefaultValue = React.useCallback(
    (nextValue) => {
      setQuestion(selectedItem.id, {
        ...question,
        default_value: nextValue,
      })
    },
    [question, selectedItem, setQuestion]
  )

  const validate = React.useCallback(
    async (value: any) => {
      if (!value) {
        setError('')
        return
      }

      try {
        if (question.question_type.question_subtype === 'phone') {
          await phoneSchema.validate(value)
        }
        if (question.question_type.question_subtype === 'email') {
          await emailSchema.validate(value)
        }

        setError('')
      } catch (e) {
        setError(e.message)
      }
    },
    // $FlowFixMe
    [question.question_type]
  )

  React.useEffect(() => {
    validate(question.default_value)
  }, [question.default_value, validate])

  const handleChange = (value: any) => {
    onChangeDefaultValue(value)
  }

  if (question.question_type.question_subtype === 'phone') {
    return (
      <div>
        <SingleLinePhone
          block
          errorLabel={error}
          hasError={!!error}
          label="Default Value"
          name="default-value"
          onBlur={(e) => validate(e.target.value)}
          onChange={(value) => handleChange(value)}
          placeholder="Default Value"
          type="phone"
          value={question.default_value ?? ''}
        />
      </div>
    )
  }

  if (question.question_type.question_subtype === 'multi_line') {
    return (
      <TextAreaField
        label="Default Value"
        name="default-value"
        onChange={handleChange}
        placeholder="Default Value"
        value={question?.default_value ?? ''}
      />
    )
  }

  if (question.question_type.question_type === 'datetime') {
    const DateOptions = () => (
      <BlockList
        className="my-0"
        items={[
          {
            label: 'None',
            active:
              !question?.date_default_value_now &&
              [null, undefined].includes(question?.default_value),
            onClick: () => {
              onChangeDateDefaultValue(null, false)
            },
          },
          {
            label: 'Current',
            active: question?.date_default_value_now,
            onClick: () => {
              onChangeDateDefaultValue(null, true)
            },
          },
          {
            label: 'Custom',
            active:
              !question?.date_default_value_now &&
              ![null, undefined].includes(question?.default_value),
            onClick: () => {
              onChangeDateDefaultValue('', false)
            },
          },
        ]}
      />
    )

    if (
      question?.date_default_value_now ||
      [null, undefined].includes(question?.default_value)
    ) {
      return (
        <>
          <QuestionTitle htmlFor="default-value">Default Value</QuestionTitle>
          <DateOptions />
        </>
      )
    }

    if (
      ['date_only', 'month_year', 'datetime'].includes(
        question.question_type.question_subtype
      )
    ) {
      //$FlowFixMe
      const subtype = question.question_type.question_subtype
      const isDateTime = subtype === 'datetime'
      const pickerType = subtype === 'month_year' ? 'month' : null

      return (
        <>
          <QuestionTitle htmlFor="default-value">Default Value</QuestionTitle>
          <DateOptions />
          <DateField
            block
            name="default-value"
            onChange={(value) => {
              handleChange(value ? value[1] : '')
            }}
            picker={pickerType}
            placeholder="Default Value"
            showTime={isDateTime}
            value={question?.default_value ?? ''}
          />
        </>
      )
    }
    if (question.question_type.question_subtype === 'time_only') {
      return (
        <>
          <QuestionTitle htmlFor="default-value">Default Value</QuestionTitle>
          <DateOptions />
          <TimeField
            block
            label="Default Value"
            name="default-value"
            onChange={(value) => {
              handleChange(value ? value[1] : '')
            }}
            placeholder="Default Value"
            value={question?.default_value ?? ''}
          />
        </>
      )
    }
  }

  if (question.question_type.question_type === 'number') {
    if (question.question_type.question_subtype === 'number') {
      return (
        <div>
          <NumericText
            block
            errorLabel={error}
            hasError={!!error}
            label="Default Value"
            name="default-value"
            onBlur={(e) => validate(e.target.value)}
            onChange={handleChange}
            placeholder="Default Value"
            value={question?.default_value ?? ''}
          />
        </div>
      )
    }
  }

  return (
    <div>
      <TextField
        errorLabel={error}
        hasError={!!error}
        label="Default Value"
        name="default-value"
        onBlur={(e) => validate(e.currentTarget.value)}
        onChange={handleChange}
        placeholder="Default Value"
        value={question?.default_value ?? ''}
      />
    </div>
  )
}

export default BuilderSideMenuDefaultValue
